import React, { useState, useEffect, useRef } from 'react';
import AdditionalInfoFrame from '../Components/AdditionalInfoFrame/AdditionalInfoFrame';
import playerjs from 'player.js';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { IconButton, Typography, Container, Box } from '@mui/material';
import { SkipPrevious, SkipNext } from '@mui/icons-material';
import './styling/VideoPlayer.css';
import VideoSkeleton from '../Components/Skeleton/VideoSkeleton';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faLock } from "@fortawesome/free-solid-svg-icons";
import SerieDetailsModal from '../Components/SerieDetailsModal/SerieDetailsModal';
import { ModalMessage } from '../Components/ModalMessage/ModalMessage';
import { NavigationBar } from '../Components/NavigationBar/NavigationBar';
import { useSingleContent } from '../hooks/useSingleContent';
import TemplateModal from '../Components/TemplateModal/TemplateModal';
import BunnyPlayer, { getBunnyHlsUrl } from '../Components/BunnyPlayer/BunnyPlayer';
import PdfViewer from '../Components/PdfViewer/PdfViewer';

// O conteúdo é um PDF? Decidimos pela URL, e NÃO pelo `type` — os quadrinhos
// ("História em Quadrinhos") chegam da API com `content_type: 'video'` embora sejam
// PDFs no S3. Confiar no `type` faria o PDF cair no ramo de vídeo e virar um <iframe>,
// que fica EM BRANCO no WebView do Android (ele não tem visualizador de PDF).
const isPdfUrl = (url) => typeof url === 'string' && /\.pdf(\?|#|$)/i.test(url);

// Monta os selos (badges) do player a partir do acesso do conteúdo.
const buildBadges = (item) => {
    const badges = [];
    const access = Array.isArray(item.access) ? item.access.map((a) => String(a).toLowerCase()) : [];
    if (access.includes('free')) badges.push({ label: 'LIVRE', kind: 'livre' });
    else if (access.length) badges.push({ label: 'PREMIUM', kind: 'premium' });
    badges.push({ label: 'HD', kind: 'q' });
    return badges;
};

const VideoPlayer = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const playlist = location.state?.playlist || [];
    const previousPath = location.state?.from || "/";

    const { videoId } = useParams();
    
    const { content: singleContent, loading: singleContentLoading, error: singleContentError } = useSingleContent(
        playlist.length === 0 ? videoId : null
    );
    
    const effectivePlaylist = playlist.length > 0 ? playlist : (singleContent ? [singleContent] : []);
    
    const [currentIndex, setCurrentIndex] = useState(() => {
        return effectivePlaylist.findIndex(item => item.id === videoId || item.contentId === videoId);
    });
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [showSerieModal, setShowSerieModal] = useState(false);
    const [showBlockedModal, setShowBlockedModal] = useState(false);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const iframeRef = useRef(null);

    useEffect(() => {
        window.scrollTo(0, 0);

    }, []);

    useEffect(() => {
        const index = effectivePlaylist.findIndex(item => item.id === videoId || item.contentId === videoId);
        if (index !== -1) {
            setCurrentIndex(index);
            if (effectivePlaylist[index].status === 'blocked') {
                setShowBlockedModal(true);
            }
            else if (effectivePlaylist[index].type === 'serie') {
                setShowSerieModal(true);
            } else {
                setShowSerieModal(false);
            }
        }
    }, [videoId, effectivePlaylist]);

    const handlePrevious = () => {
        if (currentIndex > 0) {
            setIsTransitioning(true);
            const prevItem = effectivePlaylist[currentIndex - 1];
            setCurrentIndex(currentIndex - 1);

            if (prevItem.status === 'blocked') {
                setShowBlockedModal(true);
            }
            else if (prevItem.type === 'serie') {
                setShowSerieModal(true);
            } else {
                navigate(`/video/${prevItem.id}`, {
                    state: {
                        playlist: effectivePlaylist,
                        from: previousPath
                    }
                });
            }
        }
    };

    const handleNext = () => {
        if (currentIndex < effectivePlaylist.length - 1) {
            setIsTransitioning(true);
            const nextItem = effectivePlaylist[currentIndex + 1];
            setCurrentIndex(currentIndex + 1);

            if (nextItem.status === 'blocked') {
                setShowBlockedModal(true);
            }
            else if (nextItem.type === 'serie') {
                setShowSerieModal(true);
            } else {
                navigate(`/video/${nextItem.id}`, {
                    state: {
                        playlist: effectivePlaylist,
                        from: previousPath
                    }
                });
            }
        }
    };

    const handleVideoEnd = () => {
        if (currentIndex < effectivePlaylist.length - 1) {
            handleNext();
        }
    };

    // Mantém a referência ao handler mais recente (evita stale closure no listener do player)
    const handleVideoEndRef = useRef(handleVideoEnd);
    handleVideoEndRef.current = handleVideoEnd;

    // Player.js: ao terminar o vídeo do Bunny, avança automaticamente para o próximo da playlist
    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe || !iframe.src || !iframe.src.includes('mediadelivery.net')) return undefined;
        let player;
        try {
            player = new playerjs.Player(iframe);
            player.on('ready', () => {
                player.on('ended', () => handleVideoEndRef.current());
            });
        } catch (e) {
            // player.js indisponível para este host — auto-advance fica só no Bunny
        }
        return () => {
            try { if (player) player.off('ended'); } catch (e) { /* noop */ }
        };
    }, [currentIndex, videoId]);

    const handleSerieModalClose = () => {
        setShowSerieModal(false);

        if (currentIndex < effectivePlaylist.length - 1) {
            handleNext();
        } else if (currentIndex > 0) {
            handlePrevious();
        } else {
            navigate(previousPath);
        }
    };

    const handleBlockedModalDismiss = () => {
        setShowBlockedModal(false);

        if (currentIndex < effectivePlaylist.length - 1) {
            handleNext();
        } else if (currentIndex > 0) {
            handlePrevious();
        } else {
            navigate(previousPath);
        }
    };

    const handleBlockedModalConfirm = () => {
        const currentItem = effectivePlaylist[currentIndex];
        if (currentItem && currentItem.templateId) {
            setSelectedTemplateId(currentItem.templateId);
            setShowTemplateModal(true);
        }
        handleBlockedModalDismiss();
    };

    const handleTemplateModalClose = () => {
        setShowTemplateModal(false);
        setSelectedTemplateId(null);
    };

    useEffect(() => {
        setIsTransitioning(false);
    }, [currentIndex]);

    // Show loading when fetching single content
    if (playlist.length === 0 && singleContentLoading) {
        return <VideoSkeleton />;
    }

    // Show error when failed to fetch single content
    if (playlist.length === 0 && singleContentError) {
        return (
            <Box className="loading-container">
                <Typography variant="h5" align="center" color="error">
                    Erro ao carregar conteúdo: {singleContentError}
                </Typography>
            </Box>
        );
    }

    const currentItem = effectivePlaylist[currentIndex];

    // Vídeos do Bunny: NÃO trocamos pelo skeleton na transição — mantemos o mesmo <video>
    // montado e só trocamos a fonte (o BunnyPlayer recarrega o HLS pelo prop `url`). Isso
    // evita destruir/recriar o player, que em tela cheia no WebView do Android deixava a
    // superfície do vídeo antigo CONGELADA ao passar para o próximo.
    if (isTransitioning && !getBunnyHlsUrl(currentItem?.url)) {
        return <VideoSkeleton />;
    }

    if (!currentItem) {
        return (
            <Box className="loading-container">
                <Typography variant="h5" align="center" color="error">
                    Conteúdo não encontrado
                </Typography>
            </Box>
        );
    }


    if (currentItem.type === 'serie') {
        return (
            <SerieDetailsModal
                serieId={currentItem.id}
                onClose={handleSerieModalClose}
            />
        );
    }

    if (currentItem.status === 'blocked') {
        return (
            <div className="video-player-wrapper">
                <NavigationBar
                    onBackClick={() => navigate(previousPath)}
                    title={currentItem.title}
                />

                <div className="video-player-container">
                    <div className="video-container blocked-content" onClick={() => setShowBlockedModal(true)}>
                        <div className="thumbnail-container">
                            <img src={currentItem.thumbnailUrl} alt={currentItem.title} className="blocked-thumbnail" />
                            <div className="lock-overlay">
                                <FontAwesomeIcon icon={faLock} size="2x" />
                            </div>
                        </div>
                    </div>

                    <div className="controls-container">
                        <IconButton
                            className="previous-button"
                            onClick={handlePrevious}
                            disabled={currentIndex === 0}
                            size="large"
                        >
                            <SkipPrevious />
                        </IconButton>

                        <Typography variant="h6" className="video-title">
                            {currentItem.title}
                        </Typography>

                        <IconButton
                            className="next-button"
                            onClick={handleNext}
                            disabled={currentIndex === effectivePlaylist.length - 1}
                            size="large"
                        >
                            <SkipNext />
                        </IconButton>
                    </div>
                </div>

                {showBlockedModal && (
                    <ModalMessage
                        title="Conteúdo indisponível"
                        description="Este conteúdo não está disponível no seu plano atual. Deseja fazer um upgrade?"
                        leftButtonText="Sim"
                        leftButtonAction={handleBlockedModalConfirm}
                        rightButtonText="Não"
                        rightButtonAction={handleBlockedModalDismiss}
                    />
                )}
            </div>
        );
    }

    const getEmbedUrl = (url) => {
        if (!url) return '';

        // Bunny Stream — a URL de embed (iframe.mediadelivery.net) já vem pronta para iframe
        if (url.includes('mediadelivery.net')) {
            return url;
        }

        const pandaMatch = url.match(/v=([^&]+)/);
        if (pandaMatch) {
            return url;
        }

        const youtubeMatch = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
        if (youtubeMatch) {
            return `https://www.youtube.com/embed/${youtubeMatch[1]}?autoplay=1`;
        }

        const vimeoMatch = url.match(/vimeo\.com\/(?:.*\/)?([0-9]+)/);
        if (vimeoMatch) {
            return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
        }
        return url;
    };

    const renderContent = () => {
        // PDF vem PRIMEIRO, antes de olhar o `type`: os quadrinhos são PDFs rotulados
        // como 'video' na API. O leitor desenha o PDF em canvas (PDF.js), então funciona
        // também no WebView do Android — onde o <iframe> anterior ficava em branco.
        // NÃO reusa `.pdf-container`: ele tem `overflow: hidden` + `min-height: 80vh`
        // (feito para o iframe) e cortaria as páginas, que empilham em rolagem.
        if (isPdfUrl(currentItem.url)) {
            return <PdfViewer url={currentItem.url} title={currentItem.title} />;
        }

        if (currentItem.type === 'document') {
            return (
                <div className="pdf-container">
                    <iframe
                        src={currentItem.url}
                        style={{
                            width: '100%',
                            height: '80vh',
                            border: 'none',
                            borderRadius: '8px'
                        }}
                        title={`PDF: ${currentItem.title}`}
                        allow="fullscreen"
                    />
                </div>
            );
        } else if (currentItem.type === 'video') {
            if (getBunnyHlsUrl(currentItem.url)) {
                // Bunny Stream — player customizado da marca (HLS nativo, sem iframe →
                // toca dentro do WebView e com controle total do visual)
                return (
                    <BunnyPlayer
                        url={currentItem.url}
                        poster={currentItem.thumbnailUrl}
                        title={currentItem.title}
                        subtitle={currentItem.subtitle}
                        badges={buildBadges(currentItem)}
                        hasNext={currentIndex < effectivePlaylist.length - 1}
                        onNext={handleNext}
                        onEnded={handleVideoEnd}
                    />
                );
            }
            // Legado (Panda / YouTube / Vimeo) — mantém o iframe
            return (
                <div className="video-container">
                    <iframe
                        ref={iframeRef}
                        src={getEmbedUrl(currentItem.url)}
                        style={{ border: 'none' }}
                        allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture"
                        allowFullScreen
                        fetchpriority="high"
                    />
                </div>
            );
        }
        // Fallback for unknown types - assume video
        return (
            <div className="video-container">
                <iframe
                    src={getEmbedUrl(currentItem.url)}
                    style={{ border: 'none' }}
                    allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture"
                    allowFullScreen
                    fetchpriority="high"
                />
            </div>
        );
    };

    return (
        <div className="video-player-wrapper">
            <NavigationBar
                onBackClick={() => navigate(previousPath)}
                title={currentItem.title}
            />

            <div className="video-player-container">
                {currentItem.url && renderContent()}

                {/* Player Bunny já tem controles próprios (próximo/tela cheia); só mostra
                    a barra antiga para os formatos legados (PDF, YouTube, etc.) */}
                {!(currentItem.type === 'video' && getBunnyHlsUrl(currentItem.url)) && (
                    <div className="controls-container">
                        <IconButton
                            className="previous-button"
                            onClick={handlePrevious}
                            disabled={currentIndex === 0}
                            size="large"
                        >
                            <SkipPrevious />
                        </IconButton>

                        <Typography variant="h6" className="video-title">
                            {currentItem.title}
                        </Typography>

                        <IconButton
                            className="next-button"
                            onClick={handleNext}
                            disabled={currentIndex === effectivePlaylist.length - 1}
                            size="large"
                        >
                            <SkipNext />
                        </IconButton>
                    </div>
                )}

                {currentItem.additionalInfo && (
                    <div className="video-content">
                        <AdditionalInfoFrame html={currentItem.additionalInfo} />
                    </div>
                )}
            </div>

            {showBlockedModal && (
                <ModalMessage
                    title="Conteúdo indisponível"
                    description="Este conteúdo não está disponível no seu plano atual. Deseja fazer um upgrade?"
                    leftButtonText="Sim"
                    leftButtonAction={handleBlockedModalConfirm}
                    rightButtonText="Não"
                    rightButtonAction={handleBlockedModalDismiss}
                />
            )}

            {showTemplateModal && selectedTemplateId && (
                <TemplateModal
                    templateId={selectedTemplateId}
                    onClose={handleTemplateModalClose}
                />
            )}
        </div>
    );
};

export default VideoPlayer; 